import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBProjectLocation, ProjectLocationStatus } from "@/lib/db-types";

/**
 * Lokasjoner på prosjekter (VIB-2940). Logg skrives av DB-triggeren, i
 * prosjektets Logg-fane og i lokasjonens historikk, med den ekte brukeren
 * som aktør. Avsluttede prosjekter er låst av trg_assert_writable.
 */

/** Koblingene på et prosjekt. Lokasjonsdataene leses fra oversikten. */
export function useProjectLocations(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["project-locations", projectId],
    enabled: !!projectId && enabled,
    queryFn: async (): Promise<DBProjectLocation[]> => {
      const { data, error } = await supabase.from("project_locations").select("*").eq("project_id", projectId!);
      if (error) throw error;
      return (data ?? []) as DBProjectLocation[];
    },
  });
}

export interface LocationProjectLink extends DBProjectLocation {
  project: { id: string; name: string; project_code: string | null; project_status: string } | null;
}

/** Prosjektene en lokasjon er koblet til, uten slettede prosjekter. */
export function useLocationProjects(locationId: string | undefined) {
  return useQuery({
    queryKey: ["location-projects", locationId],
    enabled: !!locationId,
    queryFn: async (): Promise<LocationProjectLink[]> => {
      const { data, error } = await supabase
        .from("project_locations")
        .select("*, project:projects!inner(id, name, project_code, project_status, deleted_at)")
        .eq("location_id", locationId!)
        .is("project.deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as LocationProjectLink[];
    },
  });
}

/**
 * Legger lokasjoner på et prosjekt som Forslag. Finnes koblingen fra før,
 * hoppes den over. legacyText kobler en historikklinje fra verktøyet.
 */
export function useAddLocationsToProject() {
  const qc = useQueryClient();
  const { selfUser } = useImpersonation();
  return useMutation({
    mutationFn: async ({
      projectId,
      locationIds,
      legacyText,
    }: {
      projectId: string;
      locationIds: string[];
      legacyText?: string;
    }): Promise<{ added: number; existing: number }> => {
      if (!selfUser) throw new Error("Fant ikke innlogget bruker");
      const { data, error } = await supabase
        .from("project_locations")
        .upsert(
          locationIds.map((location_id) => ({
            project_id: projectId,
            location_id,
            created_by: selfUser.id,
            legacy_text: legacyText ?? null,
          })),
          { onConflict: "project_id,location_id", ignoreDuplicates: true },
        )
        .select("id");
      if (error) throw new Error(`Kunne ikke legge på prosjektet: ${error.message}`);
      const added = (data ?? []).length;
      if (legacyText && added === 0) {
        // Lokasjonen lå allerede på prosjektet: knytt historikklinjen til koblingen.
        const { data: upd, error: updError } = await supabase
          .from("project_locations")
          .update({ legacy_text: legacyText })
          .eq("project_id", projectId)
          .eq("location_id", locationIds[0])
          .select("id");
        if (updError) throw new Error(`Kunne ikke koble historikken: ${updError.message}`);
        if ((upd ?? []).length !== 1) throw new Error("Lokasjonen ligger på prosjektet fra før, og du kan ikke endre den koblingen");
      }
      return { added, existing: locationIds.length - added };
    },
    onSuccess: async (_r, { projectId, locationIds }) => {
      await invalidateFor(qc, "project_locations", { projectId, locationId: locationIds.length === 1 ? locationIds[0] : undefined });
    },
  });
}

export type ProjectLocationPatch = Partial<Pick<DBProjectLocation, "start_date" | "end_date" | "note">> & {
  status?: ProjectLocationStatus;
};

export function useUpdateProjectLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ link, patch }: { link: Pick<DBProjectLocation, "id" | "project_id" | "location_id">; patch: ProjectLocationPatch }) => {
      const { data, error } = await supabase.from("project_locations").update(patch).eq("id", link.id).select("id");
      if (error) throw new Error(`Kunne ikke lagre: ${error.message}`);
      if ((data ?? []).length !== 1) throw new Error("Bare den som la til lokasjonen, PL, rådgiver eller admin kan endre den");
    },
    onSuccess: async (_r, { link }) => {
      await invalidateFor(qc, "project_locations", { projectId: link.project_id, locationId: link.location_id });
    },
  });
}

export function useRemoveProjectLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (link: Pick<DBProjectLocation, "id" | "project_id" | "location_id">) => {
      const { data, error } = await supabase.from("project_locations").delete().eq("id", link.id).select("id");
      if (error) throw new Error(`Kunne ikke fjerne: ${error.message}`);
      if ((data ?? []).length !== 1) throw new Error("Bare den som la til lokasjonen, PL, rådgiver eller admin kan fjerne den");
    },
    onSuccess: async (_r, link) => {
      await invalidateFor(qc, "project_locations", { projectId: link.project_id, locationId: link.location_id });
    },
  });
}
